import { cn } from "@/core/utils/custom-styles";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import {
  Checkbox,
  InputSearch,
  ListItem,
  Radio,
  Spinner,
} from "@woba/ds-web-next";
import type { CSSProperties, KeyboardEvent, UIEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

/** Uma opção do DsSelectMenu (mesmo formato do InputSelect do DS + descrição). */
export interface DsSelectMenuOption {
  value: string;
  label: string;
  /** Linha secundária (ex.: e-mail do colaborador). */
  description?: string;
}

export interface DsSelectMenuSearch {
  value: string;
  onChange: (text: string) => void;
  placeholder: string;
}

export interface DsSelectMenuProps {
  /** id do campo (gatilho `combobox`). O listbox usa `${id}-listbox`. */
  id: string;
  /** Rótulo acima do campo (Label/Xsmall). Sem ele, o placeholder nomeia o campo. */
  label?: string;
  placeholder: string;
  /** Texto do campo quando há seleção; sem ele, mostra o placeholder. */
  displayValue?: string | null;
  options: DsSelectMenuOption[];
  /** Valores selecionados. */
  selected: string[];
  /** Múltipla seleção (Checkbox do DS); senão, seleção única (Radio do DS). */
  multiple?: boolean;
  onSelect: (option: DsSelectMenuOption) => void;
  /** Busca no topo do menu (InputSearch do DS). O consumidor filtra/consulta. */
  search?: DsSelectMenuSearch;
  emptyText: string;
  loading?: boolean;
  disabled?: boolean;
  /** Chamado ao chegar ao fim da lista (scroll infinito). */
  onEndReached?: () => void;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

const MENU_MAX_HEIGHT = 320;

const MENU_SHADOW = [
  "0px 8px 8px -4px var(--woba-color-elevation-shadow-overlay-02)",
  "0px 20px 24px -4px var(--woba-color-elevation-shadow-overlay-01)",
].join(", ");

const FOCUS_RING =
  "0 0 0 1px var(--woba-color-elevation-focus-ring-01), 0 0 0 3px var(--woba-color-elevation-focus-ring-02)";

const labelStyle: CSSProperties = {
  fontFamily:
    'var(--woba-typography-label-xsmall-font-family, "Manrope", sans-serif)',
  fontWeight: 700,
  fontSize: "var(--woba-typography-label-xsmall-font-size, 14px)",
  lineHeight: "var(--woba-typography-label-xsmall-line-height, 14px)",
  color: "var(--woba-color-content-default)",
};

const inputTextStyle: CSSProperties = {
  fontFamily:
    'var(--woba-typography-label-input-xsmall-font-family, "Manrope", sans-serif)',
  fontWeight: 500,
  fontSize: "var(--woba-typography-label-input-xsmall-font-size, 14px)",
  lineHeight: "var(--woba-typography-label-input-xsmall-line-height, 20px)",
};

const captionStyle: CSSProperties = {
  fontFamily:
    'var(--woba-typography-caption-medium-font-family, "Manrope", sans-serif)',
  fontWeight: 500,
  fontSize: 14,
  color: "var(--woba-color-content-subtle)",
};

/**
 * Seta do campo. Largura/altura também no `style` (e numa caixa
 * inline-flex, como no InputSelect do DS): há um `svg { width: 100% }` global
 * injetado em runtime na página que, só com os atributos, esticava a seta
 * pelo campo todo e zerava a largura do texto.
 */
function Caret({ open, color }: { open: boolean; color: string }) {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        width: 20,
        height: 20,
      }}
    >
      <svg
        width={20}
        height={20}
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden
        style={{
          display: "block",
          width: 20,
          minWidth: 20,
          height: 20,
          transform: open ? "rotate(180deg)" : "none",
          transition: "transform 150ms ease",
        }}
      >
        <path
          d="M5 7.5L10 12.5L15 7.5"
          stroke={color}
          strokeWidth="1.67"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

/**
 * Marca (Checkbox/Radio do DS) só visual: a linha (`option`) é que trata o
 * clique e o teclado, então a marca fica fora da ordem de foco e do clique.
 */
function SelectionMark({
  multiple,
  checked,
}: {
  multiple: boolean;
  checked: boolean;
}) {
  return (
    <span
      aria-hidden
      className="inline-flex pointer-events-none"
      ref={(element) => element?.setAttribute("inert", "")}
    >
      {multiple ? <Checkbox checked={checked} /> : <Radio selected={checked} />}
    </span>
  );
}

/**
 * Campo de seleção com o visual do InputSelect do Workbase DS (campo pill de
 * 40px + menu do tamanho do campo, raio/sombra do Popover do DS, linhas do
 * ListItem com divisor e Checkbox/Radio do DS à direita), para filtros que o
 * InputSelect não atende: busca no topo, listas longas (scroll no menu),
 * carregamento e scroll infinito (Colaboradores, Centros de custo, Cidades,
 * Espaços).
 */
export function DsSelectMenu({
  id,
  label,
  placeholder,
  displayValue,
  options,
  selected,
  multiple = false,
  onSelect,
  search,
  emptyText,
  loading = false,
  disabled = false,
  onEndReached,
  onOpenChange,
  className,
}: DsSelectMenuProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const contentRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listboxId = `${id}-listbox`;
  const optionId = (index: number) => `${id}-opt-${index}`;
  const filled = Boolean(displayValue);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next) setActive(-1);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  const selectOption = useCallback(
    (option: DsSelectMenuOption) => {
      onSelect(option);
      if (!multiple) handleOpenChange(false);
    },
    [onSelect, multiple, handleOpenChange],
  );

  // Mantém a linha ativa visível e anunciada a partir da busca.
  useEffect(() => {
    if (!open) return;
    const searchInput =
      contentRef.current?.querySelector<HTMLInputElement>("input");
    if (active >= 0) {
      searchInput?.setAttribute("aria-activedescendant", optionId(active));
      document.getElementById(optionId(active))?.scrollIntoView?.({
        block: "nearest",
      });
    } else {
      searchInput?.removeAttribute("aria-activedescendant");
    }
  }, [active, open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (active >= options.length) setActive(options.length - 1);
  }, [options.length, active]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const isTyping = event.target instanceof HTMLInputElement;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActive((prev) => Math.min(prev + 1, options.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActive((prev) => Math.max(prev - 1, 0));
        break;
      case "Home":
        if (isTyping) return;
        event.preventDefault();
        setActive(0);
        break;
      case "End":
        if (isTyping) return;
        event.preventDefault();
        setActive(options.length - 1);
        break;
      case "Enter":
      case " ":
        if (event.key === " " && isTyping) return;
        if (active >= 0 && options[active]) {
          event.preventDefault();
          selectOption(options[active]);
        }
        break;
      case "Tab":
        handleOpenChange(false);
        break;
    }
  };

  const handleScroll = (event: UIEvent<HTMLUListElement>) => {
    if (!onEndReached || loading) return;
    const target = event.currentTarget;
    const diff = Math.abs(
      target.scrollHeight - target.scrollTop - target.clientHeight,
    );
    if (diff < 5) onEndReached();
  };

  const borderColor = open
    ? "var(--woba-color-stroke-common-active)"
    : "var(--woba-color-stroke-default)";

  return (
    <div className={cn("flex flex-col gap-1 min-w-0", className)}>
      {label && (
        <div className="flex items-center px-4 py-0.5 min-h-5">
          <label htmlFor={id} style={labelStyle}>
            {label}
          </label>
        </div>
      )}

      <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            id={id}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-label={label ? undefined : placeholder}
            disabled={disabled}
            onKeyDown={(event) => {
              if (!open && event.key === "ArrowDown") {
                event.preventDefault();
                handleOpenChange(true);
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              width: "100%",
              height: 40,
              padding: "8px 16px",
              borderRadius: "var(--woba-radii-full, 999px)",
              border: `1px solid ${borderColor}`,
              // Como no InputSelect do DS: o fundo não muda no hover nem
              // aberto (só borda + anel de foco).
              background: disabled
                ? "var(--woba-color-surface-common-disabled)"
                : "var(--woba-color-surface-default)",
              cursor: disabled ? "not-allowed" : "pointer",
              textAlign: "left",
              boxShadow: open ? FOCUS_RING : "none",
              transition: "border-color 120ms ease, box-shadow 120ms ease",
              boxSizing: "border-box",
              outline: "none",
            }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: disabled
                  ? "var(--woba-color-content-common-disabled)"
                  : filled
                    ? "var(--woba-color-content-default)"
                    : "var(--woba-color-content-subtle)",
                ...inputTextStyle,
              }}
            >
              {filled ? displayValue : placeholder}
            </span>
            <Caret
              open={open}
              color={
                disabled
                  ? "var(--woba-color-content-common-disabled)"
                  : "var(--woba-color-content-default)"
              }
            />
          </button>
        </PopoverPrimitive.Trigger>

        <PopoverPrimitive.Content
          ref={contentRef}
          align="start"
          sideOffset={4}
          collisionPadding={16}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            const target =
              contentRef.current?.querySelector<HTMLInputElement>("input") ??
              listRef.current;
            target?.focus();
          }}
          onKeyDown={handleKeyDown}
          className="z-50 outline-none"
          style={{
            width: "var(--radix-popover-trigger-width)",
            backgroundColor: "var(--woba-color-surface-default)",
            border: "1px solid var(--woba-color-stroke-default)",
            borderRadius: "var(--woba-radii-x4)",
            boxShadow: MENU_SHADOW,
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {search && (
            <div
              className="p-2"
              style={{
                borderBottom: "1px solid var(--woba-color-stroke-default)",
              }}
            >
              <InputSearch
                id={`${id}-search`}
                style={{ width: "100%" }}
                placeholder={search.placeholder}
                value={search.value}
                onChangeText={(text) => {
                  setActive(-1);
                  search.onChange(text);
                }}
              />
            </div>
          )}

          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            tabIndex={-1}
            aria-label={label ?? placeholder}
            aria-multiselectable={multiple || undefined}
            aria-busy={loading || undefined}
            onScroll={handleScroll}
            className="overflow-y-auto overscroll-contain outline-none [scrollbar-width:thin]"
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              maxHeight: MENU_MAX_HEIGHT,
            }}
          >
            {options.map((option, index) => {
              const isSelected = selected.includes(option.value);
              return (
                <li
                  key={`${option.value}-${index}`}
                  id={optionId(index)}
                  role="option"
                  aria-selected={isSelected}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectOption(option)}
                  onMouseEnter={() => setActive(index)}
                  style={{
                    cursor: "pointer",
                    background:
                      index === active
                        ? "var(--woba-color-surface-common-hover)"
                        : "transparent",
                  }}
                >
                  <ListItem
                    primary={option.label}
                    description={option.description}
                    selected={isSelected}
                    dividers={index < options.length - 1 || loading}
                    trailing={
                      <SelectionMark multiple={multiple} checked={isSelected} />
                    }
                    style={
                      index === active
                        ? { background: "transparent" }
                        : undefined
                    }
                  />
                </li>
              );
            })}

            {loading && (
              <li
                role="presentation"
                className="flex justify-center items-center py-3"
              >
                <Spinner size="sm" />
              </li>
            )}

            {!loading && options.length === 0 && (
              <li
                role="presentation"
                className="px-4 py-3"
                style={captionStyle}
              >
                {emptyText}
              </li>
            )}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Root>
    </div>
  );
}
